import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Tabs, Input, Table, Dropdown, Tag, message, Image, Modal, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    SearchOutlined,
    MoreOutlined,
    EditOutlined,
    StarOutlined,
    CopyOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import NoTemplate from '../../imgs/no-template.png';
import TreatmentPlanModal from '../TreatmentPlanModal';
import ViewTemplateModal from '../ViewTemplateModal';
import dayjs from 'dayjs';
import s from './s.module.less';
import { TreatmentTemplate } from 'types/treatmentTemplate';
import useGetTreatmentTemplates from 'hooks/videoSetting/useGetTreatmentTemplates';
import {
    deleteTreatmentTemplate,
    updateTreatmentTemplate,
    getTreatmentTemplateCounts,
    copyTreatmentTemplate,
} from 'api/videoSetting';
import { getSpecialtiesConditions } from 'api/applicationForm';
import useProviderKlarityServiceList from 'hooks/useProviderKlarityServiceList';

const VisitTypeMap = {
    'New patient initial visit': 'Initial visit',
    'Established patient visit': 'Follow-up visit',
    PRESCRIPTION_REFILL: 'Prescription refill',
};

const TreatmentPlanTemplate = () => {
    const [providerServiceList, loading] = useProviderKlarityServiceList();
    const [specialtiesOptions, setSpecialtiesOptions] = useState<{ label: string; key: string }[]>([]);
    const [treatmentTemplateCounts, setTreatmentTemplateCounts] = useState<{ serviceType: string; count: number }[]>(
        [],
    );
    const [selectedSpecialty, setSelectedSpecialty] = useState<string>();
    const [selectedVisitType, setSelectedVisitType] = useState<string>();
    const [searchText, setSearchText] = useState<string>('');

    const [templates, total, templatesLoading, fetchTemplates] = useGetTreatmentTemplates();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [showViewTemplateModal, setShowViewTemplateModal] = useState(false);

    const [selectedTemplateId, setSelectedTemplateId] = useState<number | undefined>(undefined);

    const initialVisitCount =
        treatmentTemplateCounts.find((item) => item.serviceType === 'New patient initial visit')?.count || 0;
    const followUpCount =
        treatmentTemplateCounts.find((item) => item.serviceType === 'Established patient visit')?.count || 0;
    const prescriptionRefillCount =
        treatmentTemplateCounts.find((item) => item.serviceType === 'PRESCRIPTION_REFILL')?.count || 0;

    const AllServiceTypes = [
        {
            label: `Initial visit ${initialVisitCount > 0 ? `(${initialVisitCount})` : ''}`,
            value: 'New patient initial visit',
        },
        { label: `Follow-up ${followUpCount > 0 ? `(${followUpCount})` : ''}`, value: 'Established patient visit' },
        {
            label: `Prescription refill ${prescriptionRefillCount > 0 ? `(${prescriptionRefillCount})` : ''}`,
            value: 'PRESCRIPTION_REFILL',
        },
    ];

    const fetchTreatmentTemplateCounts = async () => {
        if (!selectedSpecialty) return;
        try {
            const result = await getTreatmentTemplateCounts(selectedSpecialty);
            if (result && !result.error) {
                setTreatmentTemplateCounts(result.data?.data || []);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const refetchTemplates = useCallback(() => {
        if (!selectedSpecialty || !selectedVisitType) return;
        fetchTemplates({
            specialty: selectedSpecialty,
            serviceType: selectedVisitType,
            current: 1,
            size: 50,
            orderKey: 'updatedAt',
            orderType: 'desc',
        });
    }, [selectedSpecialty, selectedVisitType]);

    useEffect(() => {
        refetchTemplates();
    }, [selectedSpecialty, selectedVisitType]);

    useEffect(() => {
        if (selectedSpecialty) {
            fetchTreatmentTemplateCounts();
        }
    }, [selectedSpecialty]);

    useEffect(() => {
        const options: string[] = [];
        providerServiceList?.forEach((item) => {
            if (item.serviceSpecialtyName && !options.includes(item.serviceSpecialtyName)) {
                options.push(item.serviceSpecialtyName);
            }
        });
        if (selectedSpecialty && !options.includes(selectedSpecialty)) {
            setSelectedSpecialty(undefined);
        }
        setSpecialtiesOptions(options.map((item) => ({ label: item, key: item })));
        setSelectedSpecialty(options[0]);
        setSelectedVisitType(AllServiceTypes[0].value);
    }, [providerServiceList]);

    const handleCreateTemplate = () => {
        setSelectedTemplateId(undefined);
        setIsModalOpen(true);
    };

    const handleEdit = (template: TreatmentTemplate) => {
        setSelectedTemplateId(template.id);
        setIsModalOpen(true);
    };

    const handleSetAsDefault = async (template: TreatmentTemplate) => {
        try {
            const result = await updateTreatmentTemplate(template.id, { isDefault: true });
            if (result && !result.error) {
                refetchTemplates();
                message.success('Template set as default successfully');
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleDuplicate = async (template: TreatmentTemplate) => {
        try {
            const result = await copyTreatmentTemplate(template.id);
            if (result && !result.error) {
                refetchTemplates();
                fetchTreatmentTemplateCounts();
                message.success('Template duplicated successfully');
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleDelete = (template: TreatmentTemplate) => {
        Modal.confirm({
            title: template.isDefault ? 'Delete default template' : 'Delete template',
            content: template.isDefault
                ? `This template is currently set as the default for ${VisitTypeMap[selectedVisitType as keyof typeof VisitTypeMap] || selectedVisitType} in ${selectedSpecialty}. Deleting it will remove it as the default template for this appointment type.`
                : `This action will permanently delete ${template.name}. It will no longer be available for future appointments.`,
            onOk: async () => {
                try {
                    const result = await deleteTreatmentTemplate(template.id);
                    if (result && !result.error) {
                        refetchTemplates();
                        fetchTreatmentTemplateCounts();
                        message.success('Template deleted successfully');
                    }
                } catch (error) {
                    console.error(error);
                }
            },
            okText: 'Delete',
            okButtonProps: {
                danger: true,
            },
            onCancel: () => { },
            icon: null,
            width: 560,
            closable: true,
            wrapClassName: s.customConfirmRoot,
        });
    };

    const getDropdownMenu = (template: TreatmentTemplate) => {
        if (template.isSystem) {
            return [
                {
                    key: 'set-default',
                    label: 'Set as default',
                    onClick: () => handleSetAsDefault(template),
                    disabled: template.isDefault,
                },
                {
                    key: 'duplicate',
                    label: 'Duplicate',
                    onClick: () => handleDuplicate(template),
                },
            ];
        }
        return [
            {
                key: 'edit',
                label: 'Edit',
                onClick: () => handleEdit(template),
            },
            {
                key: 'set-default',
                label: 'Set as default',
                onClick: () => handleSetAsDefault(template),
                disabled: template.isDefault,
            },
            {
                key: 'duplicate',
                label: 'Duplicate',
                onClick: () => handleDuplicate(template),
            },
            {
                key: 'delete',
                label: 'Delete',
                onClick: () => handleDelete(template),
            },
        ];
    };

    const columns: ColumnsType<TreatmentTemplate> = [
        {
            title: 'TEMPLATE NAME',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: TreatmentTemplate) => (
                <div className={s.templateName}>
                    <span>{text}</span>
                    {record.isDefault && (
                        <Tag color="blue" className={s.defaultTag}>
                            Default
                        </Tag>
                    )}
                </div>
            ),
        },
        {
            title: 'LAST UPDATED',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            sorter: (a, b) => dayjs(a.updatedAt).valueOf() - dayjs(b.updatedAt).valueOf(),
            render: (time: string) => dayjs(time).format('MM/DD/YYYY'),
        },
        {
            title: 'CREATED DATE',
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
            render: (time: string) => dayjs(time).format('MM/DD/YYYY'),
        },
        {
            title: '',
            key: 'action',
            width: 120,
            render: (_: any, record: TreatmentTemplate) => (
                <div className={s.actionColumn}>
                    <Button
                        type="link"
                        className={s.viewButton}
                        onClick={() => {
                            setSelectedTemplateId(record.id);
                            setShowViewTemplateModal(true);
                        }}
                    >
                        View
                    </Button>
                    <Dropdown menu={{ items: getDropdownMenu(record) }} trigger={['hover']}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M12 5L12 5.01M12 12L12 12.01M12 19L12 19.01M12 6C11.4477 6 11 5.55228 11 5C11 4.44772 11.4477 4 12 4C12.5523 4 13 4.44772 13 5C13 5.55228 12.5523 6 12 6ZM12 13C11.4477 13 11 12.5523 11 12C11 11.4477 11.4477 11 12 11C12.5523 11 13 11.4477 13 12C13 12.5523 12.5523 13 12 13ZM12 20C11.4477 20 11 19.5523 11 19C11 18.4477 11.4477 18 12 18C12.5523 18 13 18.4477 13 19C13 19.5523 12.5523 20 12 20Z"
                                stroke="#374151"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </Dropdown>
                </div>
            ),
        },
    ];

    // 根据 searchText 过滤模板数据
    const filteredTemplates = useMemo(() => {
        if (!searchText.trim()) {
            return templates;
        }
        return templates.filter((template) => template.name.toLowerCase().includes(searchText.toLowerCase()));
    }, [templates, searchText]);

    return (
        <div className={s.wrap}>
            <div className={s.header}>
                <h1 className={s.title}>Visit Notes template</h1>
                <Button type="primary" className={s.createButton} onClick={handleCreateTemplate}>
                    Create template
                </Button>
            </div>

            <p className={s.description}>Create reusable templates to auto-fill visit notes during video visits.</p>

            <Spin spinning={loading}>
                <div className={s.content}>
                    <div className={s.tabsContainer}>
                        <Tabs
                            activeKey={selectedSpecialty}
                            onChange={setSelectedSpecialty}
                            items={specialtiesOptions}
                            className={s.specialtyTabs}
                        />
                        <div className={s.visitTypeContainer}>
                            <div className={s.visitTypeTabs}>
                                {AllServiceTypes.map((item) => (
                                    <div
                                        className={`${s.visitTypeTab} ${selectedVisitType === item.value ? s.active : ''}`}
                                        key={item.value}
                                        onClick={() => setSelectedVisitType(item.value)}
                                    >
                                        {item.label}
                                    </div>
                                ))}
                            </div>
                            <Input
                                prefix={<SearchOutlined />}
                                placeholder="Search templates"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                className={s.searchInput}
                                allowClear
                            />
                        </div>
                    </div>

                    {filteredTemplates.length === 0 && !templatesLoading ? (
                        <div className={s.emptyState}>
                            <Image width={264} height={174} src={NoTemplate} alt="No template" preview={false} />
                            <h3 className={s.emptyTitle}>No template yet</h3>
                            <p className={s.emptyDescription}>
                                Create a template to streamline documentation for future visits
                            </p>
                            <Button type="primary" className={s.emptyCreateButton} onClick={handleCreateTemplate}>
                                Create template
                            </Button>
                        </div>
                    ) : (
                        <div className={s.tableContainer}>
                            <Table
                                columns={columns}
                                dataSource={filteredTemplates}
                                loading={templatesLoading}
                                rowKey="id"
                                className={s.table}
                                pagination={false}
                            />
                        </div>
                    )}
                </div>
            </Spin>
            <TreatmentPlanModal
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onSuccess={() => {
                    setIsModalOpen(false);
                    refetchTemplates();
                    fetchTreatmentTemplateCounts();
                }}
                templateId={selectedTemplateId}
                specialties={specialtiesOptions}
            />
            <ViewTemplateModal
                open={showViewTemplateModal}
                onCancel={() => setShowViewTemplateModal(false)}
                templateId={selectedTemplateId || 0}
                specialtyName={specialtiesOptions.find((item) => item.key === selectedSpecialty)?.label || ''}
                onReopen={() => setShowViewTemplateModal(true)}
            />
        </div>
    );
};

export default TreatmentPlanTemplate;
